import React, { useCallback } from 'react';

import { Dropdown, Icon } from '@column/column-ui-kit';
import { type DataTableColumnDef, Timestamp as UITimestamp } from '@column/ui';

import { TableTimeFormat } from '~/repositories/UserRepository';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { formatDateTime, getFormattedDate } from '~/util';

export const useTimeFormat = (tableId: string) => {
  const { timeFormat, update } = useTableConfig(tableId);
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);

  const onTimeFormatChange = useCallback(
    (value: TableTimeFormat) => {
      update({ timeFormat: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save time format preference' });
      });
    },
    [addDangerNotification, update]
  );

  return { timeFormat, onTimeFormatChange };
};

export const TimeHeader: React.FC<{
  label?: React.ReactNode;
  timeFormat: TableTimeFormat;
  onTimeFormatChange: (value: TableTimeFormat) => void;
}> = ({ label = 'Created', timeFormat, onTimeFormatChange }) => (
  <Dropdown
    size="small"
    positionRight
    active={timeFormat}
    options={[
      { label: 'Relative Time', value: TableTimeFormat.Relative },
      { label: 'Date / Time', value: TableTimeFormat.Absolute },
    ]}
    onChange={onTimeFormatChange}
    customLabel={
      <span className="flex items-center gap-1">
        {label} <Icon.ChevronDown />
      </span>
    }
  />
);

export const Timestamp: React.FC<{
  date?: string | Date | null;
  fallback?: React.ReactNode;
  timeFormat?: TableTimeFormat;
}> = ({ date, fallback = null, timeFormat = TableTimeFormat.Relative }) =>
  date ? (
    <UITimestamp
      date={date}
      align="right"
      className="text-xs text-muted-foreground"
      formatHover={(hoverDate) =>
        formatDateTime(hoverDate, { timeZoneFormat: timeFormat === TableTimeFormat.Relative ? 'local' : 'utc' })
      }
    >
      {timeFormat === TableTimeFormat.Relative
        ? getFormattedDate(date, 'time-passed')
        : formatDateTime(date, { timeZoneFormat: 'local' })}
    </UITimestamp>
  ) : (
    <>{fallback}</>
  );

export interface TimestampColumnOptions<T extends Record<string, any>> {
  accessor: keyof T & string;
  label?: React.ReactNode;
  timeFormat: TableTimeFormat;
  onTimeFormatChange: (value: TableTimeFormat) => void;
  width?: string;
  className?: string;
  fallback?: React.ReactNode;
}

export const getTimestampColumn = <T extends Record<string, any>>({
  accessor,
  label = 'Created',
  timeFormat,
  onTimeFormatChange,
  width = 'min-content',
  className,
  fallback,
}: TimestampColumnOptions<T>): DataTableColumnDef<T> => ({
  header: <TimeHeader label={label} timeFormat={timeFormat} onTimeFormatChange={onTimeFormatChange} />,
  accessor,
  width,
  className,
  cell: ({ value }) => <Timestamp date={value} timeFormat={timeFormat} fallback={fallback} />,
});
